import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Circle, useMap } from "react-leaflet";
import L from "leaflet";
import { tileUrl, tileAttribution, TILE_MAX_ZOOM } from "@/lib/mapTiles";
import { useTheme } from "@/contexts/ThemeContext";
import { Play, Pause, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

// Live radar overlay tiles served free by RainViewer (no API key, ~10 min update cadence).
const RAINVIEWER_API = "https://api.rainviewer.com/public/weather-maps.json";

const youAreHereIcon = L.divIcon({
  className: "",
  html: `<div style="
    width:18px;height:18px;border-radius:50%;
    background:rgba(56,189,248,0.95);
    box-shadow:0 0 0 6px rgba(56,189,248,0.25),0 0 0 12px rgba(56,189,248,0.12);
    border:2px solid white;"></div>`,
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

function FlyToCenter({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center) {
      // If the current zoom is wider than the desired city-level zoom, snap in tighter.
      const target = Math.max(map.getZoom() || 0, zoom || 10);
      map.setView(center, target);
    }
  }, [center, zoom, map]);
  return null;
}

function fmtTs(ts) {
  if (!ts) return "—";
  return new Date(ts * 1000).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export default function LiveRadar({ lat, lon, height = 560 }) {
  const { theme } = useTheme();
  const [frames, setFrames] = useState([]); // [{path,time}]
  const [nowcast, setNowcast] = useState([]);
  const [host, setHost] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const [playing, setPlaying] = useState(false); // default OFF — show real-time latest frame
  const timerRef = useRef(null);

  // Fetch frame index every 5 min
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await fetch(RAINVIEWER_API, { cache: "no-store" });
        const j = await r.json();
        if (cancelled) return;
        setHost(j.host);
        const past = j.radar?.past || [];
        const fut = j.radar?.nowcast || [];
        setFrames(past);
        setNowcast(fut);
        // Lock onto latest observed frame unless user is actively scrubbing
        setActiveIdx((curr) => {
          // If user hasn't picked anything yet (frames was empty), pick latest
          if (past.length && curr >= past.length + fut.length) return past.length - 1;
          return past.length ? past.length - 1 : curr;
        });
      } catch (_e) { /* ignore */ }
    };
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const allFrames = useMemo(() => [...frames, ...nowcast], [frames, nowcast]);

  // Animation timer (slower, smoother — 900ms dwell)
  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (!playing || allFrames.length === 0) return;
    timerRef.current = setInterval(() => {
      setActiveIdx((i) => (i + 1) % allFrames.length);
    }, 900);
    return () => clearInterval(timerRef.current);
  }, [playing, allFrames.length]);

  const activeFrame = allFrames[activeIdx];
  const tileTemplate = activeFrame && host
    ? `${host}${activeFrame.path}/256/{z}/{x}/{y}/2/1_1.png`
    : null;

  // Auto-zoom level based on whether we have a user location.
  // RainViewer radar tiles cap around z8 globally — keep within their cache to avoid
  // "Zoom Level Not Supported" placeholders while still framing the user's metro area.
  const initialZoom = lat != null ? 8 : 4;
  const center = lat != null && lon != null ? [lat, lon] : [39.8283, -98.5795];
  const isNowcast = activeIdx >= frames.length;

  return (
    <div className="relative w-full" style={{ height }}>
      <MapContainer
        center={center}
        zoom={initialZoom}
        style={{ height: "100%", width: "100%" }}
        zoomControl={true}
        scrollWheelZoom={true}
        data-testid="live-radar-map"
      >
        <TileLayer
          key={`base-${theme}`}
          url={tileUrl(theme)}
          attribution={tileAttribution()}
          maxZoom={TILE_MAX_ZOOM}
        />
        {tileTemplate && (
          <TileLayer
            key={activeFrame.path}
            url={tileTemplate}
            opacity={0.75}
            zIndex={400}
            maxNativeZoom={7}
            maxZoom={TILE_MAX_ZOOM}
          />
        )}
        {lat != null && lon != null && (
          <>
            <Marker position={[lat, lon]} icon={youAreHereIcon} />
            <Circle center={[lat, lon]} radius={50000} pathOptions={{ color: "#38BDF8", weight: 1, opacity: 0.5, fillOpacity: 0.04 }} />
          </>
        )}
        <FlyToCenter center={lat != null ? [lat, lon] : null} zoom={8} />
      </MapContainer>

      {/* Bottom timeline / scrubber */}
      <div className="absolute left-3 right-3 bottom-3 z-[500] bg-card/85 backdrop-blur border border-border rounded-md p-3" data-testid="radar-controls">
        <div className="flex items-center gap-2">
          <Button size="icon" variant="ghost" onClick={() => setPlaying((p) => !p)} data-testid="radar-play-btn">
            {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </Button>
          <Button size="icon" variant="ghost" onClick={() => setActiveIdx((i) => (i - 1 + allFrames.length) % Math.max(1, allFrames.length))}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button size="icon" variant="ghost" onClick={() => setActiveIdx((i) => (i + 1) % Math.max(1, allFrames.length))}>
            <ChevronRight className="w-4 h-4" />
          </Button>
          <div className="font-mono text-xs flex-1 flex items-center gap-2">
            <span className={isNowcast ? "text-amber-400" : "text-primary"}>
              {isNowcast ? "FORECAST" : "OBSERVED"}
            </span>
            <span className="text-muted-foreground">{fmtTs(activeFrame?.time)}</span>
            <span className="text-muted-foreground/60 ml-auto">frame {activeIdx + 1}/{allFrames.length || 0}</span>
          </div>
        </div>
        {/* Frame ticks */}
        <div className="mt-2 flex gap-0.5 h-1.5">
          {allFrames.map((f, i) => (
            <button
              key={f.path}
              type="button"
              onClick={() => { setPlaying(false); setActiveIdx(i); }}
              className="flex-1 rounded-sm transition-colors"
              style={{
                background:
                  i === activeIdx
                    ? "hsl(199 89% 55%)"
                    : i >= frames.length
                    ? "rgba(245,158,11,0.35)"
                    : "rgba(148,163,184,0.25)",
              }}
              title={fmtTs(f.time)}
            />
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="absolute top-3 left-3 z-[500] bg-card/85 backdrop-blur border border-border rounded-md px-3 py-2 text-[10px] font-mono tracking-wider">
        <div className="text-primary mb-1">// LIVE RADAR</div>
        <div className="flex items-center gap-3 text-muted-foreground">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm" style={{ background: "#0EA5E9" }} /> light</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm" style={{ background: "#10B981" }} /> moderate</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm" style={{ background: "#F59E0B" }} /> heavy</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm" style={{ background: "#EF4444" }} /> extreme</span>
        </div>
      </div>
    </div>
  );
}
